
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function CodeComplianceInspectionsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Code Compliance Inspections',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Electrical code compliance inspections in Fresno, CA. Verify your property meets California Title 24, NEC, and Fresno building code requirements.',
    url: `${siteUrl}/services/testing-certification/code-compliance-inspections`,
  };

  const features = [
    {
      icon: 'ri-file-list-3-line',
      title: 'California Title 24 Compliance',
      description:
        'Verify your electrical system meets California energy efficiency and safety standards',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'NEC Standards Verification',
      description:
        'Ensure compliance with National Electrical Code requirements and best practices',
    },
    {
      icon: 'ri-government-line',
      title: 'Fresno Building Code',
      description:
        'Check compliance with local Fresno building department electrical requirements',
    },
    {
      icon: 'ri-file-text-line',
      title: 'Permit Documentation',
      description:
        'Inspection reports accepted by Fresno building departments for permit applications',
    },
    {
      icon: 'ri-tools-line',
      title: 'Violation Identification',
      description:
        'Identify code violations and provide clear guidance for bringing systems into compliance',
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Renovation Compliance',
      description:
        'Ensure electrical work for renovations and additions meets current code requirements',
    },
    {
      icon: 'ri-alert-line',
      title: 'Safety Code Review',
      description:
        'Verify GFCI/AFCI protection, grounding, bonding, and other safety code requirements',
    },
    {
      icon: 'ri-file-check-line',
      title: 'Compliance Certification',
      description:
        'Detailed reports certifying code compliance for permits, insurance, and real estate',
    },
  ];

  const whatsIncluded = [
    'Licensed C-10 electrician inspection',
    'California Title 24 compliance review',
    'NEC standards verification',
    'Fresno building code compliance check',
    'GFCI/AFCI protection verification',
    'Grounding and bonding inspection',
    'Panel and circuit code compliance',
    'Violation identification and guidance',
    'Permit-ready documentation',
    'Detailed compliance report',
  ];

  return (
    <>
      <SEOHead
        title="Code Compliance Inspections Fresno CA | Electrical Code Verification"
        description="Electrical code compliance inspections in Fresno, CA. Verify California Title 24, NEC, and Fresno building code compliance. Permit-ready reports from licensed electricians."
        canonical="/services/testing-certification/code-compliance-inspections"
        keywords="electrical code compliance Fresno, code compliance inspection, California Title 24 electrical, Fresno building code"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Code Compliance Inspections — Fresno, CA"
        heroSubtitle="Verify your electrical system meets California Title 24, NEC, and Fresno building codes"
        heroImage="https://public.readdy.ai/ai/img_res/32993f979ce2e179038204b5c831aa3f.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20electrical%20code%20compliance%20checklist%20and%20California%20Title%2024%20documentation%20on%20clipboard%20next%20to%20digital%20multimeter%20and%20testing%20tools%20in%20front%20of%20residential%20electrical%20panel%20bright%20interior%20setting&width=700&height=600&seq=code-comp-fresno-001&orientation=portrait"
        fresnoContent="Fresno property owners need code compliance inspections for permits, renovations, insurance, and real estate transactions. Our licensed C-10 electricians verify your electrical system meets California Title 24 energy standards, National Electrical Code (NEC) requirements, and Fresno building department regulations. Whether you're in Woodward Park, Tower District, or anywhere in Fresno County, we provide detailed compliance reports accepted by building departments, insurance companies, and real estate professionals."
        features={features}
        whatsIncluded={whatsIncluded}
        faqs={[
          {
            question: 'What electrical codes apply to Fresno properties?',
            answer:
              'Fresno properties must comply with California Title 24 (energy efficiency and safety standards), the National Electrical Code (NEC), and local Fresno building department requirements. California adopts the NEC with state-specific amendments. Our inspections verify compliance with all applicable codes for residential and commercial properties in Fresno.',
          },
          {
            question: 'Why do I need a code compliance inspection in Fresno?',
            answer:
              'Code compliance inspections are required for building permits, major renovations, additions, insurance claims, and real estate transactions in Fresno. They verify your electrical system is safe and meets current standards. Non-compliant electrical work can fail inspections, void insurance, create safety hazards, and reduce property value in Fresno.',
          },
          {
            question: 'How much does a code compliance inspection cost in Fresno?',
            answer:
              'Code compliance inspections in Fresno typically cost $250–$500 depending on property size and scope of review. Commercial properties and complex systems may cost more. We provide upfront pricing and detailed reports accepted by Fresno building departments for permit applications and final inspections.',
          },
          {
            question: 'What does a code compliance inspection in Fresno include?',
            answer:
              'Our Fresno code compliance inspections review electrical panels, circuit breakers, wiring methods, grounding and bonding, GFCI/AFCI protection, outlet and switch installations, lighting, and overall system design against California Title 24, NEC, and Fresno building code requirements. We identify violations and provide guidance for bringing systems into compliance.',
          },
          {
            question: 'Can you help me pass a Fresno building department inspection?',
            answer:
              'Yes. If your electrical work failed a Fresno building department inspection, we can identify the code violations, explain what needs to be corrected, and perform the necessary repairs to bring your system into compliance. We work directly with Fresno building inspectors to ensure your project passes final inspection.',
          },
          {
            question: 'Do older Fresno homes need to meet current electrical codes?',
            answer:
              'Existing electrical systems in older Fresno homes are generally grandfathered under the codes in effect when they were installed. However, any new electrical work, renovations, or additions must meet current California Title 24 and NEC standards. When selling a home, buyers may require upgrades to current codes. Our inspections identify which upgrades are required vs. recommended.',
          },
        ]}
      />
    </>
  );
}
